import React from 'react'
import { Card, CardHeader, CardContent, Button } from '@/shared/ui'
import { type CryptoAsset } from '@/modules/crypto-assets/services/cryptoAssetsService'

type Props = {
  items: CryptoAsset[]
  loading: boolean
  paging: boolean
  hasMore: boolean
  onLoadMore: () => void
  renderItem: (a: CryptoAsset) => React.ReactNode
}

export function AssetsInfiniteGrid({ items, loading, paging, hasMore, onLoadMore, renderItem }: Props) {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const canObserve = !loading && hasMore && !paging

  React.useEffect(() => {
    if (!canObserve) return
    const el = sentinelRef.current
    if (!el) return
    let ticking = false
    const obs = new IntersectionObserver((entries) => {
      const e = entries[0]
      if (e && e.isIntersecting && !ticking) {
        ticking = true
        onLoadMore()
        setTimeout(() => { ticking = false }, 400)
      }
    }, { root: null, rootMargin: '200px 0px', threshold: 0 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [canObserve, onLoadMore])

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="text-base font-medium">Ativos cadastrados</div>
          <div className="flex items-center gap-2">
            {/* Espaço reservado para ações adicionais no topo */}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-foreground/60">Carregando…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-foreground/60">Nenhum ativo cadastrado.</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((a) => renderItem(a))}
          </div>
        )}

        {(paging || hasMore) && (
          <div ref={sentinelRef} className="mt-3 h-8 w-full text-center text-xs text-foreground/60">
            {paging ? 'Carregando mais…' : ''}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

